import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Checkbox, FormControlLabel, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import AddIcon from '@material-ui/icons/Add';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import DateField from 'ui-kit/inputs/DateField/DateFieldString';
import { FormStateModel } from './types';
import { US_STATE_OPTIONS } from './constants';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import * as yup from 'yup';

const STATE_ENTRY_ID_PREFIX = 'state';
const GEORGIA_STATE_CODE = 'GA';
const FORM_TYPE_POLICY = 'POLICY';
const FIELD_STATE = 'state' as const;
const FIELD_FACC_EXT_PROD_ID = 'faccExtProdId' as const;
const MAX_FACC_FIELD_LENGTH = 50;

const useStyles = makeStyles((theme) => ({
  drawerContent: {
    width: '100%',
  },
  stateCard: {
    marginBottom: theme.spacing(2),
    position: 'relative',
  },
  stateCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  stateLabel: {
    fontWeight: 600,
    fontSize: '16px',
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  addStateButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textTransform: 'none',
    justifyContent: 'flex-start',
    paddingLeft: 0,
  },
  fieldWrapper: {
    marginBottom: theme.spacing(2),
  },
  checkboxWrapper: {
    marginTop: theme.spacing(1),
  },
}));

interface AddStateDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (states: FormStateModel[]) => void;
  formType: string;
  existingStates: FormStateModel[];
  editingState?: FormStateModel | null;
  onSaveEdit?: (state: FormStateModel) => void;
}

interface StateEntry extends FormStateModel {
  id: string;
}

const stateEntrySchema = yup.object().shape({
  state: yup.string().required('State is required'),
  stateName: yup.string().required('State name is required'),
  faccProductId: yup.string().max(MAX_FACC_FIELD_LENGTH, `Max length is ${MAX_FACC_FIELD_LENGTH}`).nullable(),
  faccExtProdId: yup.string().max(MAX_FACC_FIELD_LENGTH, `Max length is ${MAX_FACC_FIELD_LENGTH}`).nullable(),
  faccExtendedCoverageId: yup.string().max(MAX_FACC_FIELD_LENGTH, `Max length is ${MAX_FACC_FIELD_LENGTH}`).nullable(),
  activeDate: yup.string().nullable(),
  inactiveDate: yup.string().nullable(),
  eJacketsEnabled: yup.boolean(),
  hasBrandId: yup.boolean(),
});

const createEmptyStateEntry = (): StateEntry => ({
  id: `${STATE_ENTRY_ID_PREFIX}-${Date.now()}-${Math.random()}`,
  state: '',
  stateName: '',
  faccProductId: '',
  faccExtProdId: '',
  faccExtendedCoverageId: '',
  activeDate: '',
  inactiveDate: '',
  eJacketsEnabled: false,
  hasBrandId: false,
});

const AddStateDrawer: React.FC<AddStateDrawerProps> = ({
  open,
  onClose,
  onSave,
  formType,
  existingStates,
  editingState = null,
  onSaveEdit,
}) => {
  const classes = useStyles();
  const isEditMode = !!editingState;

  const [stateEntries, setStateEntries] = useState<StateEntry[]>(() =>
    editingState
      ? [
          {
            id: `${STATE_ENTRY_ID_PREFIX}-${Date.now()}-${Math.random()}`,
            state: editingState.state,
            stateName: editingState.stateName,
            faccProductId: editingState.faccProductId,
            faccExtProdId: editingState.faccExtProdId,
            faccExtendedCoverageId: editingState.faccExtendedCoverageId,
            activeDate: editingState.activeDate,
            inactiveDate: editingState.inactiveDate,
            eJacketsEnabled: editingState.eJacketsEnabled,
            hasBrandId: editingState.hasBrandId,
          },
        ]
      : [createEmptyStateEntry()],
  );
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [isShowValidation, setIsShowValidation] = useState<boolean>(false);

  // Reset state entries when drawer opens or editingState changes
  useEffect(() => {
    if (open) {
      if (editingState) {
        setStateEntries([
          {
            id: `${STATE_ENTRY_ID_PREFIX}-${Date.now()}-${Math.random()}`,
            state: editingState.state,
            stateName: editingState.stateName,
            faccProductId: editingState.faccProductId,
            faccExtProdId: editingState.faccExtProdId,
            faccExtendedCoverageId: editingState.faccExtendedCoverageId,
            activeDate: editingState.activeDate,
            inactiveDate: editingState.inactiveDate,
            eJacketsEnabled: editingState.eJacketsEnabled,
            hasBrandId: editingState.hasBrandId,
          },
        ]);
      } else {
        setStateEntries([createEmptyStateEntry()]);
      }
      setValidationErrors([]);
      setIsShowValidation(false);
    }
  }, [open, editingState]);

  const addStateEntry = () => {
    setStateEntries([...stateEntries, createEmptyStateEntry()]);
  };

  const removeStateEntry = (id: string) => {
    if (stateEntries.length > 1) {
      setStateEntries(stateEntries.filter((entry) => entry.id !== id));
    }
  };

  const updateStateEntry = (id: string, field: keyof FormStateModel, value: string | boolean) => {
    setStateEntries(
      stateEntries.map((entry) => {
        if (entry.id === id) {
          const updated = { ...entry, [field]: value };

          // When state code is selected, also populate stateName
          if (field === FIELD_STATE && typeof value === 'string') {
            const selectedState = US_STATE_OPTIONS.find((option) => option.value === value);
            updated.stateName = selectedState?.name || '';
          }

          // Clear Extended Coverage ID if Ext Prod ID is cleared
          if (field === FIELD_FACC_EXT_PROD_ID && !value) {
            updated.faccExtendedCoverageId = '';
          }

          // Clear Has Brand ID if state is not GA
          if (field === FIELD_STATE && String(value).toUpperCase() !== GEORGIA_STATE_CODE) {
            updated.hasBrandId = false;
          }

          return updated;
        }
        return entry;
      }),
    );
  };

  const validateAllEntries = async (): Promise<boolean> => {
    const allErrors: FieldValidationError[] = [];

    for (let i = 0; i < stateEntries.length; i++) {
      const entry = stateEntries[i];
      const errors = await doValidate(entry, stateEntrySchema);
      if (errors && errors.length > 0) {
        // Prefix field names with entry ID to make them unique
        const prefixedErrors = errors.map((err: FieldValidationError) => ({
          ...err,
          field: `${entry.id}.${err.field}`,
        }));
        allErrors.push(...prefixedErrors);
      }
    }

    setValidationErrors(allErrors);
    return allErrors.length === 0;
  };

  const handleSave = async () => {
    setIsShowValidation(true);
    const isValid = await validateAllEntries();

    if (isValid) {
      if (isEditMode && onSaveEdit) {
        // Edit mode: save single state
        const { id, ...stateData } = stateEntries[0];
        onSaveEdit(stateData);
      } else {
        // Add mode: remove id field before saving
        const statesToSave: FormStateModel[] = stateEntries.map(({ id, ...state }) => state);
        onSave(statesToSave);
      }
      onClose();
    }
  };

  const handleCancel = () => {
    // TODO: Add unsaved changes warning if data entered
    onClose();
  };

  const getFieldErrors = (entryId: string, fieldName: string): FieldValidationError[] => {
    return validationErrors.filter((err) => err.field === `${entryId}.${fieldName}`);
  };

  // Get available state options (exclude already selected states in other entries and existing states)
  const getAvailableStateOptions = (currentEntryId: string) => {
    const selectedStates = stateEntries
      .filter((entry) => entry.id !== currentEntryId && entry.state)
      .map((entry) => entry.state);
    const existingStateCodes = existingStates.map((state) => state.state);
    const unavailableStates = [...selectedStates, ...existingStateCodes];

    return US_STATE_OPTIONS.filter((option) => !unavailableStates.includes(option.value));
  };

  return (
    <FormDrawerComponent
      open={open}
      onDismissAction={handleCancel}
      onPrimaryAction={handleSave}
      primaryActionLabel="Save"
      title={isEditMode ? 'Edit State' : 'Add State'}
      width={600}
      crossQAPrefix="AddStateDrawer"
      primaryActionDataQa="AddStateDrawer-Save"
      primaryActionProps={{
        variant: 'contained',
      }}
      dismissActionProps={{}}
    >
      <Box className={classes.drawerContent}>
        {stateEntries.map((entry, index) => (
          <Card key={entry.id} className={classes.stateCard} elevation={2}>
            <CardContent>
              <Box className={classes.stateCardHeader}>
                <Typography className={classes.stateLabel}>{entry.state || `State ${index + 1}`}</Typography>
                {!isEditMode && stateEntries.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeStateEntry(entry.id)}
                    data-testid={`AddStateDrawer-DeleteState-${index}`}
                  >
                    <DeleteOutlineOutlinedIcon className={classes.deleteIcon} />
                  </IconButton>
                )}
              </Box>

              <Box className={classes.fieldWrapper}>
                <AgentNetDropdownSelector
                  fullWidth
                  label="State"
                  name={`${entry.id}.state`}
                  value={entry.state}
                  options={getAvailableStateOptions(entry.id)}
                  menuOption={(value) => updateStateEntry(entry.id, 'state', value)}
                  required
                  showValidation={isShowValidation}
                  errs={getFieldErrors(entry.id, 'state')}
                  disabled={isEditMode}
                />
              </Box>

              <Box className={classes.fieldWrapper}>
                <AgentNetTextInput
                  fullWidth
                  label="FACC Product ID"
                  name={`${entry.id}.faccProductId`}
                  value={entry.faccProductId}
                  onChange={(e) => updateStateEntry(entry.id, 'faccProductId', e.target.value)}
                  showValidation={isShowValidation}
                  errs={getFieldErrors(entry.id, 'faccProductId')}
                />
              </Box>

              <Box className={classes.fieldWrapper}>
                <AgentNetTextInput
                  fullWidth
                  label="FACC Ext Prod ID"
                  name={`${entry.id}.faccExtProdId`}
                  value={entry.faccExtProdId}
                  onChange={(e) => updateStateEntry(entry.id, 'faccExtProdId', e.target.value)}
                  showValidation={isShowValidation}
                  errs={getFieldErrors(entry.id, 'faccExtProdId')}
                />
              </Box>

              <Box className={classes.fieldWrapper}>
                <AgentNetTextInput
                  fullWidth
                  label="FACC Extended Coverage ID"
                  name={`${entry.id}.faccExtendedCoverageId`}
                  value={entry.faccExtendedCoverageId}
                  onChange={(e) => updateStateEntry(entry.id, 'faccExtendedCoverageId', e.target.value)}
                  disabled={!entry.faccExtProdId}
                  showValidation={isShowValidation}
                  errs={getFieldErrors(entry.id, 'faccExtendedCoverageId')}
                />
              </Box>

              <Box className={classes.fieldWrapper}>
                <DateField
                  fullWidth
                  label="Active Date"
                  name={`${entry.id}.activeDate`}
                  value={entry.activeDate}
                  onChange={(date) => updateStateEntry(entry.id, 'activeDate', date || '')}
                  showValidation={isShowValidation}
                  errs={getFieldErrors(entry.id, 'activeDate')}
                />
              </Box>

              <Box className={classes.fieldWrapper}>
                <DateField
                  fullWidth
                  label="Inactive Date"
                  name={`${entry.id}.inactiveDate`}
                  value={entry.inactiveDate}
                  onChange={(date) => updateStateEntry(entry.id, 'inactiveDate', date || '')}
                  showValidation={isShowValidation}
                  errs={getFieldErrors(entry.id, 'inactiveDate')}
                />
              </Box>

              {formType.toUpperCase() === FORM_TYPE_POLICY && (
                <Box className={classes.checkboxWrapper}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={entry.eJacketsEnabled}
                        onChange={(e) => updateStateEntry(entry.id, 'eJacketsEnabled', e.target.checked)}
                        color="primary"
                        data-testid={`AddStateDrawer-eJacketsEnabled-${index}`}
                      />
                    }
                    label="eJackets Enabled?"
                  />
                </Box>
              )}

              {entry.state.toUpperCase() === GEORGIA_STATE_CODE && (
                <Box className={classes.checkboxWrapper}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={entry.hasBrandId}
                        onChange={(e) => updateStateEntry(entry.id, 'hasBrandId', e.target.checked)}
                        color="primary"
                        data-testid={`AddStateDrawer-HasBrandId-${index}`}
                      />
                    }
                    label="Has Brand ID?"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        ))}

        {!isEditMode && (
          <AgentNetButton
            variant="text"
            color="primary"
            onClick={addStateEntry}
            className={classes.addStateButton}
            data-testid="AddStateDrawer-AddStateButton"
            startIcon={<AddIcon />}
          >
            Add State
          </AgentNetButton>
        )}
      </Box>
    </FormDrawerComponent>
  );
};

export default AddStateDrawer;
